import {useCallback, useState} from 'react';
import {I18n} from 'aws-amplify/utils';

import {useCertificateWriteCapabilities} from './useCertificateWriteCapabilities';
import {useDeclareProviderCapabilitiesMutation} from '../../provider-connections/hooks/useProviderConnectionsQuery';
import {certificateDeclaration} from '../../provider-connections/services/grantElevation';
import {
    clearElevationDecline,
    hasDeclinedElevation,
    recordElevationDecline,
} from '../../provider-connections/services/elevationDeclines';
import {READ_ONLY_GRANTS, RoleGrantSelection} from '../../provider-connections/services/rolePolicyBuilder';
import {
    showErrorNotifications,
    showSuccessNotification,
} from '../../../components/ServiceNotifications';

/**
 * FNP-1365 — THE ASK FOR `acm:DeleteCertificate`.
 *
 * ⚠️ A SEPARATE HOOK FROM `useGrantElevation`, AND NOT BY PREFERENCE.
 *
 * The DNS hook reads the declaration off `ProviderConnection.dnsCapabilities`.
 * That route does not exist for certificates: the app has no typed
 * `certificateCapabilities` on the connection (FNP-1398 — the queries select
 * it, nothing typed it, and that file belongs to another ticket). So the DNS
 * hook seeds both certificate grants as false, which is safe for a DNS
 * correction and useless as a basis for a certificate one.
 *
 * What the certificates module DOES have is its own service, which reads
 * `certificateCapabilities.canRequest` and `.canDelete` off the connection list
 * and exposes both through `useCertificateWriteCapabilities`. Those are real
 * values, so this hook can both ASK the right question and send a truthful
 * correction. Routing certificates through the DNS hook would have shipped a
 * prompt that fires against a tenant who already holds the grant, and a
 * correction that revokes their ability to REQUEST certificates as the price of
 * granting delete.
 *
 * When FNP-1398 types the field on the connection, these two hooks can merge.
 * Until then this is the honest split rather than a duplicate.
 */
export const useCertificateGrantElevation = (connectionId: string | undefined) => {

    const {
        capabilitiesKnown,
        canRequestCertificates,
        canDeleteCertificates,
    } = useCertificateWriteCapabilities(connectionId);

    const declareMutation = useDeclareProviderCapabilitiesMutation();

    // Read once into state, not on every render — see `useGrantElevation` for
    // why a render-time storage read leaves the prompt on screen after a
    // decline.
    const [declined, setDeclined] = useState<boolean>(() =>
        connectionId ? hasDeclinedElevation(connectionId, 'deleteCertificates') : false,
    );

    /**
     * ⚠️ SILENT WHILE THE ANSWER IS UNKNOWN. `capabilitiesKnown` is false while
     * loading, on error, with no snapshot, and for a connection that is not
     * ACTIVE. Prompting in any of those states would tell a tenant they lack a
     * permission we simply have not read yet — and asking them to widen a role
     * that is already wide enough is worse than saying nothing.
     */
    const shouldPrompt = Boolean(connectionId)
        && capabilitiesKnown
        && !canDeleteCertificates
        && !declined;

    /**
     * What the connection declares today, as a grant selection, for the policy
     * preview.
     *
     * ⚠️ THE DNS HALF IS NOT READ HERE AND IS DELIBERATELY CONSERVATIVE. This
     * module cannot see `dnsCapabilities`, so the preview builds on the
     * read-only baseline plus the certificate grants that ARE known. The
     * consequence is honest and one-directional: the policy shown may omit DNS
     * actions the tenant already granted, so a tenant who pastes it verbatim
     * would narrow their own role rather than widen it.
     *
     * That is why the prompt's copy says the policy REPLACES theirs, and why
     * the certificate case is the weaker of the two until FNP-1398 lands. It is
     * recorded as a follow-up rather than papered over with an invented DNS
     * selection, which would be the same guess pointed at the more dangerous
     * direction.
     */
    const currentSelection: RoleGrantSelection = {
        ...READ_ONLY_GRANTS,
        requestCertificates: canRequestCertificates,
        deleteCertificates: canDeleteCertificates,
    };

    const decline = useCallback(() => {
        if (connectionId) {
            recordElevationDecline(connectionId, 'deleteCertificates');
        }
        setDeclined(true);
    }, [connectionId]);

    const grantIt = useCallback(async () => {
        if (!connectionId) {
            return;
        }
        try {
            /**
             * ⚠️ THE CERTIFICATE BLOCK CARRIES BOTH FIELDS, AND `canRequest` IS
             * THE ONE THAT MATTERS HERE. A correction states the COMPLETE
             * intended state — the API reads an omitted field as "keep" and an
             * explicit false as "revoke" — so sending only `canDelete: true`
             * would be fine, but sending `canRequest: false` because we never
             * looked would strip a grant the tenant is actively using.
             *
             * `canRequestCertificates` is read from the stored declaration
             * immediately above, so this states what they have plus what they
             * are granting.
             *
             * ⚠️ AND NO `dns` BLOCK AT ALL. This module cannot read the DNS
             * declaration, so an omitted block — the API's "keep what is
             * stored" — is the only safe thing to send. Building one from the
             * read-only baseline above would revoke every DNS grant the tenant
             * holds, which is exactly the silent revocation
             * `buildDeclareInput`'s certificate rule exists to prevent, aimed
             * the other way.
             */
            await declareMutation.mutateAsync({
                id: connectionId,
                certificate: certificateDeclaration(canRequestCertificates, true),
            });

            clearElevationDecline(connectionId, 'deleteCertificates');
            setDeclined(false);

            showSuccessNotification(I18n.get('Permission recorded. You can try that again now.'));

        } catch (e) {
            // Not rethrown — called straight from an onClick with nobody to
            // catch it. See `useGrantElevation`.
            showErrorNotifications(e);
        }
    }, [connectionId, canRequestCertificates, declareMutation]);

    return {
        shouldPrompt,
        currentSelection,
        granted: canDeleteCertificates,
        declined,
        decline,
        grantIt,
        isSaving: declareMutation.isPending,
    };
};
